import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Amorce, AmorcesService } from '../../../services/amorces.service';
import {
  GroupeAmorces,
  GroupeAmorcesPayload,
  GroupesAmorcesService,
} from '../../../services/groupes-amorces.service';

@Component({
  selector: 'app-groupes-amorces',
  imports: [CommonModule, FormsModule],
  templateUrl: './groupes-amorces.component.html',
  styleUrl: './groupes-amorces.component.scss'
})
export class GroupesAmorcesComponent implements OnInit {
  protected pageMode: 'creer' | 'modifier' | 'supprimer' = 'supprimer';
  protected groupes: GroupeAmorces[] = [];
  protected amorces: Amorce[] = [];

  protected selectedId = '';
  protected deletingId: number | null = null;

  protected nouveauNom = '';
  protected nouvellesAmorceIds: number[] = [];

  protected nomEdition = '';
  protected amorceIdsEdition: number[] = [];

  protected loading = false;
  protected loadingList = false;
  protected loadingAmorces = false;
  protected success = '';
  protected error = '';

  constructor(
    private readonly groupesService: GroupesAmorcesService,
    private readonly amorcesService: AmorcesService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncPageMode();
    this.router.events.subscribe((event: unknown) => {
      if (event instanceof NavigationEnd) {
        this.syncPageMode();
        this.loadGroupes();
      }
    });

    this.loadAmorces();
    this.loadGroupes();
  }

  protected get showCreateSection(): boolean {
    return this.pageMode === 'creer';
  }

  protected get showEditSection(): boolean {
    return this.pageMode === 'modifier';
  }

  protected get showListSection(): boolean {
    return this.pageMode === 'modifier' || this.pageMode === 'supprimer';
  }

  protected get showDeleteActionsInTable(): boolean {
    return this.pageMode === 'supprimer';
  }

  protected getAmorceLabel(amorce: Amorce): string {
    const nom = (amorce.nom_officiel ?? '').trim();
    return nom ? `${amorce.code} - ${nom}` : amorce.code;
  }

  protected isCreateAmorceChecked(amorceId: number): boolean {
    return this.nouvellesAmorceIds.includes(amorceId);
  }

  protected toggleCreateAmorce(amorceId: number, checked: boolean): void {
    this.nouvellesAmorceIds = this.toggleId(this.nouvellesAmorceIds, amorceId, checked);
  }

  protected isEditAmorceChecked(amorceId: number): boolean {
    return this.amorceIdsEdition.includes(amorceId);
  }

  protected toggleEditAmorce(amorceId: number, checked: boolean): void {
    this.amorceIdsEdition = this.toggleId(this.amorceIdsEdition, amorceId, checked);
  }

  protected createGroupe(): void {
    if (!this.nouveauNom.trim()) {
      this.error = 'Le nom est requis.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: GroupeAmorcesPayload = {
      nom: this.nouveauNom.trim(),
      amorce_ids: [...this.nouvellesAmorceIds].sort((a, b) => a - b),
    };

    this.zone.run(() => {
      this.groupesService.create(payload).subscribe({
        next: () => {
          this.success = 'Groupe d\'amorces créé avec succès.';
          this.resetCreateForm();
          this.loadGroupes();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la création: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected onSelectChange(): void {
    const selected = this.groupes.find((groupe) => groupe.id === Number(this.selectedId));
    this.nomEdition = selected?.nom ?? '';
    this.amorceIdsEdition = selected ? [...selected.amorce_ids] : [];
  }

  protected updateGroupe(): void {
    if (!this.selectedId) {
      this.error = 'Veuillez sélectionner un groupe d\'amorces.';
      return;
    }

    if (!this.nomEdition.trim()) {
      this.error = 'Le nom est requis.';
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';

    const payload: GroupeAmorcesPayload = {
      nom: this.nomEdition.trim(),
      amorce_ids: [...this.amorceIdsEdition].sort((a, b) => a - b),
    };

    this.zone.run(() => {
      this.groupesService.update(Number(this.selectedId), payload).subscribe({
        next: () => {
          this.success = 'Groupe d\'amorces modifié avec succès.';
          this.loadGroupes();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la modification: ${this.getErrorMessage(err)}`;
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  protected deleteGroupeRow(groupe: GroupeAmorces): void {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer le groupe d'amorces "${groupe.nom}" ?`)) {
      return;
    }

    this.deletingId = groupe.id;
    this.error = '';
    this.success = '';

    this.zone.run(() => {
      this.groupesService.delete(groupe.id).subscribe({
        next: () => {
          this.success = 'Groupe d\'amorces supprimé avec succès.';
          this.loadGroupes();
          if (this.selectedId === String(groupe.id)) {
            this.selectedId = '';
            this.clearEditForm();
          }
          this.deletingId = null;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors de la suppression: ${this.getErrorMessage(err)}`;
          this.deletingId = null;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private syncPageMode(): void {
    const lastPart = this.router.url.split('/').pop() ?? '';
    if (['creer', 'modifier', 'supprimer'].includes(lastPart)) {
      this.pageMode = lastPart as 'creer' | 'modifier' | 'supprimer';
    } else {
      this.pageMode = 'supprimer';
    }
  }

  private loadAmorces(): void {
    this.loadingAmorces = true;
    this.zone.run(() => {
      this.amorcesService.list().subscribe({
        next: (amorces: Amorce[]) => {
          this.amorces = amorces;
          this.loadingAmorces = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement des amorces: ${this.getErrorMessage(err)}`;
          this.loadingAmorces = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadGroupes(): void {
    this.loadingList = true;
    this.error = '';

    this.zone.run(() => {
      this.groupesService.list().subscribe({
        next: (groupes: GroupeAmorces[]) => {
          this.groupes = groupes;
          if (this.selectedId) {
            this.onSelectChange();
          }
          this.loadingList = false;
          this.cdr.markForCheck();
        },
        error: (err: HttpErrorResponse) => {
          this.error = `Erreur lors du chargement: ${this.getErrorMessage(err)}`;
          this.loadingList = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  private clearEditForm(): void {
    this.nomEdition = '';
    this.amorceIdsEdition = [];
  }

  private resetCreateForm(): void {
    this.nouveauNom = '';
    this.nouvellesAmorceIds = [];
  }

  private toggleId(source: number[], id: number, checked: boolean): number[] {
    if (checked) {
      if (source.includes(id)) {
        return source;
      }
      return [...source, id];
    }

    return source.filter((current) => current !== id);
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }

    if (err.error?.detail) {
      return err.error.detail;
    }

    if (err.error?.message) {
      return err.error.message;
    }

    return err.statusText || 'Erreur inconnue';
  }
}
